<template>
  <v-container fluid class="inbox-container">
    <!-- Compose New Message Button -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-btn color="primary" @click="openNewMessageComposer">
          Compose New Message
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <!-- LEFT PANEL: Conversation List -->
      <v-col cols="12" md="4">
        <v-card class="left-pane-card" outlined>
          <v-card-title class="card-header">Messages</v-card-title>
          <v-divider></v-divider>

          <v-list ref="conversationList">
            <!-- loading placeholder -->
            <v-list-item v-if="conversationsLoading">
              <v-list-item-title>Loading conversations...</v-list-item-title>
            </v-list-item>

            <template v-else>
              <!-- animate reordering with transition-group -->
              <transition-group name="move" tag="div">
                <v-list-item
                  v-for="conv in conversations"
                  :key="conv.partner.id"
                  @click="selectConversationByPartner(conv.partner.id)"
                  :class="{ 'active-message': conv.partner.id === selectedPartnerId }"
                  class="list-item conversation-item"
                  style="cursor: pointer;"
                >
                  <div class="conversation-text">
                    <strong>{{ conv.partner.full_name }}</strong>
                    <span v-if="user.role === 'supermanager'" class="bar-name">
                      - {{ conv.partner.bar }}
                    </span>
                  </div>
                  <v-badge
                    v-if="conv.unread_count > 0"
                    :content="conv.unread_count"
                    color="red"
                    overlap
                    class="conversation-badge"
                  ></v-badge>
                  <v-list-item-subtitle>
                    {{ formatDate(conv.last_message_at) }}
                  </v-list-item-subtitle>
                </v-list-item>
              </transition-group>

              <!-- empty state when there are no conversations -->
              <v-list-item v-if="conversations.length === 0">
                <v-list-item-title>No conversations yet.</v-list-item-title>
              </v-list-item>
            </template>
          </v-list>
        </v-card>
      </v-col>

      <!-- RIGHT PANEL -->
      <v-col cols="12" md="8">
        <!-- 1. new‑message composer -->
        <div v-if="isComposingNew">
          <v-card class="new-message-card" outlined>
            <v-card-title>Create New Message</v-card-title>
            <v-card-text>
              <v-form ref="newMessageForm" v-model="newMessageValid">
                <v-autocomplete
                  v-model="newMessage.recipient"
                  v-model:search="search"
                  label="To"
                  placeholder="Type a name"
                  :items="recipients"
                  :loading="recipientLoading"
                  item-title="full_name"
                  item-value="id"
                  hide-no-data
                  hide-details
                  clearable
                  @update:search="onSearchInput"
                  required
                ></v-autocomplete>
                <div ref="quillNewMessage" class="quill-editor"></div>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-btn color="primary" @click="sendNewMessage">Send</v-btn>
              <v-btn text @click="cancelNewMessage">Cancel</v-btn>
            </v-card-actions>
          </v-card>
        </div>

        <!-- 2. loading placeholder -->
        <div v-else-if="messagesLoading" class="d-flex align-center justify-center pa-6">
          <v-progress-circular indeterminate class="mr-3"></v-progress-circular>
          Loading messages...
        </div>

        <!-- 3. conversation view -->
        <div v-else-if="selectedConversation.length" class="conversation-container">
          <div class="conversation-header">
            <h3>{{ conversationTitle }}</h3>
          </div>

          <div ref="conversationBody" class="conversation-body" @scroll="onScroll">
            <div v-if="conversationNext && atTop" class="load-more-wrapper">
              <v-btn text @click="loadMore">Load earlier messages</v-btn>
            </div>

            <div
              v-for="msg in selectedConversation"
              :key="msg.id"
              class="message-item"
              :class="{
                sent: Number(msg.sender.id) === Number(user.id),
                received: Number(msg.sender.id) !== Number(user.id)
              }"
            >
              <div class="message-meta">
                <strong>{{ displayName(msg.sender) }}</strong>
                <small> - {{ formatDate(msg.created_at) }}</small>
              </div>
              <div class="message-body" v-html="msg.body"></div>
            </div>
          </div>

          <v-card class="reply-card" outlined>
            <v-card-title>Reply</v-card-title>
            <v-card-text>
              <div ref="quillReply" class="quill-editor"></div>
            </v-card-text>
            <v-card-actions>
              <v-btn color="primary" @click="sendReply">Send Reply</v-btn>
            </v-card-actions>
          </v-card>
        </div>

        <!-- 4. default info -->
        <div v-else>
          <v-alert type="info">
            Select a conversation from the left or click "Compose New Message" to create one.
          </v-alert>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import api from "@/plugins/axios";
import Quill from "quill"; // eslint-disable-line no-unused-vars
import "quill/dist/quill.snow.css";
import { imageHandler, uploadImage } from '@/utils/quillImageUploader';
import { sanitizeContent } from '@/utils/sanitizeHelper';

export default {
  name: "InboxPage",
  data() {
    return {
      // LEFT pane summaries
      conversations: [],
      conversationsLoading: true,
      // RIGHT pane thread
      selectedConversation: [],
      selectedPartnerId: null,
      conversationPage: 1,
      conversationNext: false,
      atTop: false,
      messagesLoading: false,
      // Composer state
      isComposingNew: false,
      newMessageValid: false,
      newMessage: { recipient: "", subject: "", body: "" },
      quillNewMessage: null,
      quillReply: null,
      // User & recipients
      user: {},
      recipients: [],
      recipientLoading: false,
      search: "",
    };
  },
  computed: {
    conversationTitle() {
      if (!this.selectedConversation.length) return "";
      const first = this.selectedConversation[0];
      return Number(first.sender.id) === Number(this.user.id)
        ? first.recipient.full_name
        : first.sender.full_name;
    }
  },
  watch: {
    selectedConversation() {
      if (this.conversationPage === 1) this.scrollToBottom();
    }
  },
  methods: {
    displayName(user) {
      const fn = user.first_name || '';
      const ln = user.last_name || '';
      const full = `${fn} ${ln}`.trim();
      return full || user.username;
    },
    formatDate(d) {
      return new Date(d).toLocaleString();
    },

    // ── LEFT PANE: summaries endpoint ───────────────────────────────
    async fetchConversations() {
      this.conversationsLoading = true;
      try {
        const res = await api.get("/api/direct-messages/conversations/");
        this.conversations = res.data;
      } catch (e) {
        console.error("Could not load conversations:", e);
        this.conversations = [];
      } finally {
        this.conversationsLoading = false;
      }
    },
    // Silent refresh without loading indicator
    async fetchConversationsSilent() {
      try {
        const res = await api.get("/api/direct-messages/conversations/");
        this.conversations = res.data;
      } catch (e) {
        console.error("Silent load conversations failed:", e);
      }
    },
    // ── RIGHT PANE: load thread pages ───────────────────────────────
    async fetchConversation() {
      const limit = 20;
      const offset = (this.conversationPage - 1) * limit;
      const bodyEl = this.$refs.conversationBody;
      const oldHeight = bodyEl ? bodyEl.scrollHeight : 0;

      try {
        const r = await api.get(
          `/api/direct-messages/?conversation=${this.selectedPartnerId}&limit=${limit}&offset=${offset}`
        );
        const msgs = (r.data.results || r.data).reverse();
        // Sanitize message bodies for safe display and image proxying
        msgs.forEach(m => {
          try {
            m.body = sanitizeContent(m.body || '').toString();
          } catch (_) {
            // keep original on sanitize failure
          }
        });
        this.selectedConversation = [...msgs, ...this.selectedConversation];
        this.conversationNext = Boolean(r.data.next);

        this.$nextTick(() => {
          if (this.conversationPage === 1) {
            this.scrollToBottom();
            this.markConversationAsRead();
          } else if (bodyEl) {
            bodyEl.scrollTop = bodyEl.scrollHeight - oldHeight;
          }
          this.initReplyQuill();
        });
      } catch (e) {
        console.error(e);
      } finally {
        this.messagesLoading = false;
      }
    },

    // ── Open a thread & refresh summaries ───────────────────────────
    async selectConversationByPartner(partnerId) {
      // Cancel new-message composer if open
      if (this.isComposingNew) {
        this.cleanupNewMessageQuill();
        this.isComposingNew = false;
      }
      this.cleanupReplyQuill();
      this.selectedPartnerId = partnerId;
      this.conversationPage = 1;
      this.selectedConversation = [];
      this.conversationNext = false;
      this.messagesLoading = true;

      
      await this.fetchConversation();
      await this.markConversationAsRead ();
      await this.fetchConversationsSilent();

      this.messagesLoading = false;
    },

    // ── “Load more” and scroll handlers ─────────────────────────────
    onScroll() {
      const el = this.$refs.conversationBody;
      this.atTop = el ? el.scrollTop <= 30 : false;
    },
    loadMore() {
      if (!this.conversationNext) return;
      this.conversationPage += 1;
      this.fetchConversation();
    },

    // ── Helpers ───────────────────────────────────────────────────
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.conversationBody;
        if (el) setTimeout(() => (el.scrollTop = el.scrollHeight), 0);
      });
    },
    initReplyQuill() {
      if (!this.quillReply && this.$refs.quillReply) {
        const toolbarOptions = [
          ["bold", "italic", "underline"],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link", "image"]
        ];
        this.quillReply = new Quill(this.$refs.quillReply, {
          theme: "snow",
          modules: {
            toolbar: {
              container: toolbarOptions,
              handlers: {
                image: function() { imageHandler(this.quill); },
                link: function(value) {
                  if (value) {
                    const href = prompt('Enter the URL');
                    if (href) {
                      const formatted = href.startsWith('http://') || href.startsWith('https://') ? href : `https://${href}`;
                      this.quill.format('link', formatted);
                    }
                  } else {
                    this.quill.format('link', false);
                  }
                }
              }
            }
          }
        });
        // Paste & drag-drop → upload → served URL
        const rootRP = this.quillReply.root;
        this._rpPaste = (evt) => this.onQuillPaste(evt, this.quillReply);
        this._rpDrop  = (evt) => this.onQuillDrop(evt,  this.quillReply);
        rootRP.addEventListener('paste', this._rpPaste);
        rootRP.addEventListener('drop',  this._rpDrop);
      }
    },
    cleanupNewMessageQuill() {
      if (this.quillNewMessage) {
        const root = this.quillNewMessage.root;
        if (this._nmPaste) root.removeEventListener('paste', this._nmPaste);
        if (this._nmDrop)  root.removeEventListener('drop',  this._nmDrop);
        this._nmPaste = null;
        this._nmDrop  = null;
        this.quillNewMessage.root.innerHTML = "";
        this.quillNewMessage = null;
      }
    },
    cleanupReplyQuill() {
      if (this.quillReply) {
        const root = this.quillReply.root;
        if (this._rpPaste) root.removeEventListener('paste', this._rpPaste);
        if (this._rpDrop)  root.removeEventListener('drop',  this._rpDrop);
        this._rpPaste = null;
        this._rpDrop  = null;
        this.quillReply.root.innerHTML = "";
        this.quillReply = null;
      }
    },

    // --- Shared helpers for paste/drag image upload → served URL ---
    async quillInsertImage(quill, url) {
      if (!quill || !url) return;
      try {
        quill.focus();
        const sel = quill.getSelection();
        const index = sel ? sel.index : quill.getLength();
        quill.insertEmbed(index, 'image', url, 'user');
        quill.setSelection(index + 1, 0, 'user');
      } catch (e) {
        console.error('insert image failed', e);
      }
    },
    async handleFileList(quill, fileList) {
      if (!fileList || !fileList.length) return;
      const maxSize = 3 * 1024 * 1024; // 3MB cap
      for (const file of fileList) {
        if (!file || !file.type || !file.type.startsWith('image/')) continue;
        if (file.size > maxSize) {
          alert('Image upload failed: file size exceeds 3 MB limit.');
          continue;
        }
        try {
          const url = await uploadImage(file);
          await this.quillInsertImage(quill, url);
        } catch (err) {
          console.error('upload (paste/drop) failed', err);
        }
      }
    },
    onQuillPaste(e, quill) {
      const dt = e.clipboardData || window.clipboardData;
      if (!dt || !dt.items) return;
      const items = Array.from(dt.items).filter(i => i.kind === 'file' && i.type.startsWith('image/'));
      if (!items.length) return;
      e.preventDefault();
      const files = items.map(i => i.getAsFile()).filter(Boolean);
      this.handleFileList(quill, files);
    },
    onQuillDrop(e, quill) {
      if (!e.dataTransfer || !e.dataTransfer.files || !e.dataTransfer.files.length) return;
      const hasImage = Array.from(e.dataTransfer.files).some(f => f && f.type && f.type.startsWith('image/'));
      if (!hasImage) return;
      e.preventDefault();
      e.stopPropagation();
      this.handleFileList(quill, e.dataTransfer.files);
    },

    // ── New message composer ───────────────────────────────────────
    openNewMessageComposer() {
      this.cleanupReplyQuill();
      this.isComposingNew = true;
      this.selectedPartnerId = null;
      this.$nextTick(() => {
        if (!this.quillNewMessage && this.$refs.quillNewMessage) {
          const toolbarOptions = [
            ["bold", "italic", "underline"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link", "image"]
          ];
          this.quillNewMessage = new Quill(this.$refs.quillNewMessage, {
            theme: "snow",
            modules: {
              toolbar: {
                container: toolbarOptions,
                handlers: {
                  image: function() { imageHandler(this.quill); },
                  link: function(value) {
                    if (value) {
                      const href = prompt('Enter the URL');
                      if (href) {
                        const formatted = href.startsWith('http://') || href.startsWith('https://') ? href : `https://${href}`;
                        this.quill.format('link', formatted);
                      }
                    } else {
                      this.quill.format('link', false);
                    }
                  }
                }
              }
            }
          });
          // Paste & drag-drop → upload → served URL
          const rootNM = this.quillNewMessage.root;
          this._nmPaste = (evt) => this.onQuillPaste(evt, this.quillNewMessage);
          this._nmDrop  = (evt) => this.onQuillDrop(evt,  this.quillNewMessage);
          rootNM.addEventListener('paste', this._nmPaste);
          rootNM.addEventListener('drop',  this._nmDrop);
        }
      });
    },
    cancelNewMessage() {
      this.cleanupNewMessageQuill();
      this.isComposingNew = false;
      this.newMessage = { recipient:"", subject:"", body:"" };
      if (this.selectedConversation.length) this.initReplyQuill();
    },
    async sendNewMessage() {
      const id = this.newMessage.recipient;
      if (this.quillNewMessage) {
        const raw = this.quillNewMessage.root.innerHTML;
        // Sanitize + rewrite images to /api/images/serve before saving
        this.newMessage.body = sanitizeContent(raw).toString();
      }
      try {
        await api.post(
          "/api/direct-messages/",
          { recipient_id: id, subject: this.newMessage.subject, body: this.newMessage.body }
        );
        this.cancelNewMessage();
        await this.selectConversationByPartner(id);
      } catch (e) {
        console.error("Failed to send new message:", e);
      }
    },

    // ── Reply within a thread ──────────────────────────────────────
    async sendReply() {
      if (!this.quillReply) return;
      const raw = this.quillReply.root.innerHTML;
      // Sanitize + rewrite images to /api/images/serve
      const body = sanitizeContent(raw).toString();
      const tmpId = `tmp-${Date.now()}`;
      const partner = (this.selectedConversation[0].sender.id === this.user.id)
        ? this.selectedConversation[0].recipient
        : this.selectedConversation[0].sender;

      // optimistic UI
      this.selectedConversation.push({ id:tmpId, sender:this.user, recipient:partner, body, created_at: new Date().toISOString(), read_at:null });
      this.scrollToBottom();
      this.quillReply.root.innerHTML = "";

      try {
        const { data: savedMsg } = await api.post(
          "/api/direct-messages/",
          { body, recipient_id: partner.id }
        );
        const idx = this.selectedConversation.findIndex(m => m.id === tmpId);
        if (idx !== -1) this.selectedConversation.splice(idx,1,savedMsg);
        this.scrollToBottom();
        await this.fetchConversationsSilent();
      } catch (e) {
        console.error("Reply failed:", e);
      }
    },

    // ── Mark as read ───────────────────────────────────────────────
    async markConversationAsRead() {
      const now = new Date().toISOString();
      // Optimistically mark all loaded messages as read in the UI
      this.selectedConversation.forEach(m => {
        if (!m.read_at && Number(m.recipient.id) === Number(this.user.id)) {
          m.read_at = now;
        }
      });
      // Batch‐mark entire conversation as read via partner_id
      if (this.selectedPartnerId) {
        try {
          await api.post(
            `/api/direct-messages/mark-read-conversation/`,
            { partner_id: this.selectedPartnerId }
          );
        } catch (e) {
          console.error("Batch mark read failed:", e);
        }
      }
    },

    // ── Recipient search for composer ─────────────────────────────
    onSearchInput(v) {
      if (v && v.length>=2) this.searchRecipients(v);
      else this.recipients=[];
    },
    searchRecipients(q) {
      api.get("/api/eligible-recipients/", { params: { query: q } })
        .then(r => this.recipients = r.data)
        .catch(e => console.error(e));
    },

    // ── Bootstrap: load user & left pane ──────────────────────────
    fetchUser() {
      api.get("/api/user/")
        .then(r => {
          this.user = r.data;
          this.fetchConversations();
        })
        .catch(e => console.error(e));
    }
  },

  created() {
    this.fetchUser();
  },
  beforeUnmount() {
    this.cleanupNewMessageQuill();
    this.cleanupReplyQuill();
  }
};
</script>

<style scoped>
/* ----- your full stylesheet exactly as posted ----- */
.inbox-container {
  background: linear-gradient(to bottom, #fafafa, #e7effa, #d2e3f5);
  min-height: 100vh;
  padding: 20px;
}
.left-pane-card {
  margin-bottom: 20px;
  border-radius: 8px;
  overflow: hidden;
}
.card-header {
  background-color: #3f51b5;
  color: #fff;
  font-size: 1.25rem;
  padding: 12px 16px;
}
.list-item {
  transition: background-color 0.2s ease;
}
.list-item:hover {
  background-color: #f5f5f5;
}
.active-message {
  background-color: #d0f0ff !important;
  border-left: 4px solid #00acc1;
}
.conversation-container {
  padding: 5px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 6px;
  background-color: #fcfcfc;
}
.conversation-body {
  max-height: 400px;
  overflow-y: auto;
  padding: 8px 5px 2px;
  position: relative;
}
.message-item {
  border: 1px solid #ccc;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 10px;
}
.message-item.sent {
  background-color: #e0f7dd;
}
.message-item.received {
  background-color: #f0f7ff;
}
.quill-editor {
  background-color: #fff;
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 12px;
  min-height: 150px;
  height: auto !important;
  overflow-y: auto;
  resize: vertical;
}
.conversation-item {
  position: relative;
  padding-right: 40px;
}
.conversation-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.conversation-badge :deep(.v-badge__badge) {
  font-size: 12px;
  min-width: 25px;
  height: 25px;
  line-height: 50px;
  border-radius: 50%;
}
.conversation-badge {
  position: absolute;
  top: 50%;
  right: 30px;
  transform: translateY(-50%);
}
:deep(.quill-editor img) {
  max-width: 100% !important;
  height: auto !important;
  max-height: 250px !important;
}
:deep(.message-body img) {
  max-width: 100% !important;
  height: auto !important;
  max-height: 250px !important;
}
.load-more-wrapper {
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
  text-align: center;
  padding: 8px 0;
}
.move-move {
  transition: transform 0.3s ease;
}
/* allow Quill link tooltip to overflow and stay on top */
:deep(.ql-tooltip) {
  z-index: 10000 !important;
  overflow: visible !important;
}

/* ensure the editor container lets tooltips be visible outside */
.quill-editor {
  overflow: visible !important;
}

/* allow Quill link tooltip in new and reply cards */
.new-message-card, .reply-card {
  overflow: visible !important;
}
.new-message-card .quill-editor, .reply-card .quill-editor {
  overflow: visible !important;
}
</style>
